/**
 * Interactive Primitives
 *
 * Building blocks for user interactions:
 * - Button: Clickable button
 * - Link: External/internal link
 * - Chip: Clickable tag/chip
 * - ChipGroup: Group of selectable chips
 */

import React from 'react';

export function Button({
  text,
  icon,
  variant = 'default', // default, primary, outline, ghost
  size = 'sm', // xs, sm, md
  color, // primary, success, warning, error
  disabled,
  action, // Action identifier for onAction callback
  query, // Query to send when clicked
  onAction,
  children,
  style = {},
}) {
  const handleClick = () => {
    if (disabled) return;
    if (onAction) {
      onAction({ type: 'button', action, query, text: text || children });
    }
  };

  const sizes = {
    xs: { padding: '4px 8px', fontSize: 'var(--text-xs)' },
    sm: { padding: '6px 12px', fontSize: 'var(--text-sm)' },
    md: { padding: '8px 16px', fontSize: 'var(--text-sm)' },
  };

  const baseColor = color || 'primary';

  const variants = {
    default: {
      background: `var(--color-${baseColor})`,
      color: 'white',
      border: 'none',
    },
    primary: {
      background: `var(--color-${baseColor})`,
      color: 'white',
      border: 'none',
    },
    outline: {
      background: 'transparent',
      color: `var(--color-${baseColor})`,
      border: `1px solid var(--color-${baseColor})`,
    },
    ghost: {
      background: 'transparent',
      color: `var(--color-${baseColor})`,
      border: 'none',
    },
  };

  const v = variants[variant] || variants.default;
  const s = sizes[size] || sizes.sm;

  return (
    <button
      onClick={handleClick}
      disabled={disabled}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: '6px',
        ...s,
        ...v,
        borderRadius: 'var(--radius-md)',
        cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.5 : 1,
        fontWeight: 'var(--font-medium)',
        transition: 'all var(--transition-fast)',
        ...style,
      }}
    >
      {icon && <span>{icon}</span>}
      {text || children}
    </button>
  );
}

export function Link({
  text,
  href,
  icon,
  external,
  query, // For internal navigation/queries
  onAction,
  children,
  style = {},
}) {
  const handleClick = (e) => {
    if (query && onAction) {
      e.preventDefault();
      onAction({ type: 'link', query, text: text || children });
    }
  };

  return (
    <a
      href={href || '#'}
      onClick={handleClick}
      target={external ? '_blank' : undefined}
      rel={external ? 'noopener noreferrer' : undefined}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: '4px',
        color: 'var(--color-primary)',
        fontSize: 'var(--text-sm)',
        textDecoration: 'none',
        cursor: 'pointer',
        ...style,
      }}
    >
      {icon && <span>{icon}</span>}
      {text || children}
      {external && <span style={{ fontSize: '10px' }}>↗</span>}
    </a>
  );
}

export function Chip({
  text,
  icon,
  color = 'default', // default, primary, success, warning, error
  selected,
  clickable = true,
  onRemove,
  action,
  query,
  onAction,
  children,
  style = {},
}) {
  const handleClick = () => {
    if (!clickable) return;
    if (onAction) {
      onAction({ type: 'chip', action, query, text: text || children, selected: !selected });
    }
  };

  const colors = {
    default: {
      bg: selected ? 'var(--color-surface-hover)' : 'var(--color-surface)',
      text: 'var(--color-text)',
      border: 'var(--color-border)',
    },
    primary: {
      bg: selected ? 'var(--color-primary)' : 'rgba(var(--color-primary-rgb), 0.1)',
      text: selected ? 'white' : 'var(--color-primary)',
      border: 'var(--color-primary)',
    },
    success: {
      bg: selected ? 'var(--color-success)' : 'rgba(var(--color-success-rgb), 0.1)',
      text: selected ? 'white' : 'var(--color-success)',
      border: 'var(--color-success)',
    },
    warning: {
      bg: selected ? 'var(--color-warning)' : 'rgba(var(--color-warning-rgb), 0.1)',
      text: selected ? 'white' : 'var(--color-warning)',
      border: 'var(--color-warning)',
    },
    error: {
      bg: selected ? 'var(--color-error)' : 'rgba(var(--color-error-rgb), 0.1)',
      text: selected ? 'white' : 'var(--color-error)',
      border: 'var(--color-error)',
    },
  };

  const c = colors[color] || colors.default;

  return (
    <span
      onClick={handleClick}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: '6px',
        padding: '4px 10px',
        fontSize: 'var(--text-xs)',
        fontWeight: 'var(--font-medium)',
        borderRadius: 'var(--radius-full)',
        background: c.bg,
        color: c.text,
        border: `1px solid ${c.border}`,
        cursor: clickable ? 'pointer' : 'default',
        transition: 'all var(--transition-fast)',
        ...style,
      }}
    >
      {icon && <span>{icon}</span>}
      {text || children}
      {onRemove && (
        <span
          onClick={(e) => {
            e.stopPropagation();
            onRemove();
          }}
          style={{
            marginLeft: '2px',
            cursor: 'pointer',
            opacity: 0.7,
          }}
        >
          ×
        </span>
      )}
    </span>
  );
}

export function ChipGroup({
  chips = [], // [{ text, icon, color, query }]
  multiSelect,
  selected = [], // Array of selected indices or values
  onAction,
  style = {},
}) {
  const handleChipClick = (chip, idx) => {
    if (onAction) {
      onAction({
        type: 'chip-group',
        chip,
        index: idx,
        query: chip.query,
        action: chip.action,
      });
    }
  };

  return (
    <div
      style={{
        display: 'flex',
        flexWrap: 'wrap',
        gap: 'var(--space-2)',
        ...style,
      }}
    >
      {chips.map((chip, idx) => {
        const isSelected = selected.includes(idx) || selected.includes(chip.text) || selected.includes(chip.value);

        return (
          <Chip
            key={idx}
            text={chip.text || chip.label}
            icon={chip.icon}
            color={chip.color || 'primary'}
            selected={isSelected}
            clickable
            onAction={() => handleChipClick(chip, idx)}
          />
        );
      })}
    </div>
  );
}
